import { type ReactNode } from 'react'
import { Head, Link, usePage, usePoll } from '@inertiajs/react'
import { Breadcrumbs, type BreadcrumbEntry } from '@components/layouts/Breadcrumbs'
import { Sidebar } from '@components/layouts'
import { type NavTree } from '@components/layouts/sidebar/navTree'
import { locationFromUrl } from '@components/layouts/sidebar/activeMatch'
import { AnnouncementBanner } from '@components/molecules/Alerts/AnnouncementBanner'

type SharedProps = {
  navTree?: NavTree
  sidebarOpen?: boolean
  breadcrumbs?: BreadcrumbEntry[]
}

const POLL_INTERVAL_MS = 30_000

/**
 * `rest` schedules the next tick only once the previous request lands, so a slow
 * response can't stack requests. Split out of SidebarLayout because usePoll reads
 * `autoStart` once under empty effect deps, so mounting it alongside the sidebar
 * is what ties the poll's lifetime to the sidebar's.
 */
function PollingSidebar({ tree, open, url }: { tree: NavTree; open?: boolean; url: string }) {
  usePoll(POLL_INTERVAL_MS, { only: ['navTree'] }, { mode: 'rest' })

  return <Sidebar tree={tree} defaultOpen={open} location={locationFromUrl(url)} linkComponent={Link} />
}

/**
 * Persistent layout applied to every v2 Inertia page (see the `resolve` step in
 * entrypoints/application.jsx). It mounts the collapsible sidebar inside the
 * Inertia tree so active-item highlighting and badge counts stay in sync with
 * client-side navigation, reading `navTree`/`sidebarOpen` shared from
 * V2::ApplicationController. When they're absent (e.g. signed-out flows) it
 * renders the page on its own.
 */
export default function SidebarLayout({ children }: { children: ReactNode }) {
  const { props, url } = usePage<SharedProps>()
  const { navTree, sidebarOpen, breadcrumbs = [] } = props

  if (!navTree) return <>{children}</>

  return (
    <>
      <AnnouncementBanner />
      <PollingSidebar tree={navTree} open={sidebarOpen} url={url} />
      <Head title={breadcrumbs.at(-1)?.label} />
      <div className="tw:flex tw:min-h-dvh tw:flex-col tw:transition-[padding] tw:duration-200 tw:ease-linear tw:lg:pl-(--sb-content-offset,16rem)">
        <Breadcrumbs items={breadcrumbs} linkComponent={Link} />
        {children}
      </div>
    </>
  )
}
